import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { isCancel } from "axios";
import { useSearchParams } from "src/utils/useSearchParams";

import { RouteGetMerchflowBasePog_basePogId_ } from "@CommonApi/merchflow/base-pog/_basePogId_";
import { RouteGetMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import { RouteGetMerchflowStorePog_storePogId_previousAssortment } from "@CommonApi/merchflow/store/pog/_storePogId_/previous-assortment";
import { RouteGetAllStoreSummary } from "@CommonApi/store-summary";
import { FlowReview } from "@CommonTypes/backend/FlowReview";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { omniAtom } from "src/utils/atoms";
import { PdfPlanogramPage } from "./PdfPlanogramPage";
import { sanitizeStoreTitle } from "./singleBayUtils";

const pogAtom = omniAtom<Pog | null>({
  key: "pages/pogExportPdf/pog",
  value: null,
});

export type StoreDetail = Pick<FlowReview, "category_code" | "retailer_category_code"> & {
  store: string;
  name: string;
  go_live_date: string | null;
  template_id?: string | null;
};

export type StoreProductsMap = Record<string, string | { name: string; is_oos?: boolean }>;

export const PageMerchflows_ExportPdf_StorePogId = () => {
  const { storePogId: _storePogId, basePogId: _basePogId } = useParams<{
    storePogId?: string;
    basePogId?: string;
  }>();
  const storePogId = Number(_storePogId);
  const basePogId = Number(_basePogId);
  const { getSearchParam } = useSearchParams();
  const compareWithParam = getSearchParam("compareWith");
  const compareWith = compareWithParam ? Number(compareWithParam) : undefined;

  const {
    getMerchflowStorePogApi,
    getMerchflowBasePogApi,
    getMerchflowByFilterConfigIdApi,
    getMerchflowDetailsApi,
    getPreviousAssortmentApi,
  } = useApi();
  const { hasLoader } = useLoaders();

  const [storeDetail, setStoreDetail] = useState<StoreDetail | null>(null);
  const [pog, setPog] = useAtom(pogAtom);
  const [storeProducts, setStoreProducts] = useState<StoreProductsMap | undefined>(undefined);
  const [hasNoDeliveryHistory, setHasNoDeliveryHistory] = useState(false);
  const [hasComparisonError, setHasComparisonError] = useState(false);

  const loadPog = async () => {
    let response;
    let storeDetail: StoreDetail | null = null;
    if (storePogId) {
      response = await getMerchflowStorePogApi(storePogId, false);
      const merchflowDetail = await getMerchflowByFilterConfigIdApi(
        response?.filter_config_id || 0,
      );
      storeDetail = {
        store: response?.store || "",
        category_code: merchflowDetail?.category_code || "",
        retailer_category_code: merchflowDetail?.retailer_category_code || "",
        name: merchflowDetail?.name || "",
        go_live_date: merchflowDetail?.go_live_date || null,
        template_id: response?.template_id || null,
      };
    } else if (basePogId) {
      response = await getMerchflowBasePogApi(basePogId);
      const merchflowDetail = await getMerchflowDetailsApi(response?.merchflow_id || 0);
      storeDetail = {
        store: response?.store_code || "",
        category_code: merchflowDetail?.category_code || "",
        retailer_category_code: merchflowDetail?.retailer_category_code || "",
        name: merchflowDetail?.name || "",
        go_live_date: merchflowDetail?.go_live_date || null,
        template_id: null,
      };
    }

    if (response && response.pog) {
      setPog(new Pog(response.pog));
      setStoreDetail(storeDetail);
    }
  };

  const isLoading =
    hasLoader(RouteGetMerchflowStorePog_storePogId_) ||
    hasLoader(RouteGetAllStoreSummary) ||
    hasLoader(RouteGetMerchflowBasePog_basePogId_) ||
    hasLoader(RouteGetMerchflowStorePog_storePogId_previousAssortment);

  useEffect(() => {
    loadPog();

    return () => {
      setPog(null);
    };
  }, []);

  useEffect(() => {
    if (!storeDetail) return;

    const previousTitle = document.title;
    const sanitizedTitle = sanitizeStoreTitle(
      storeDetail.category_code,
      storeDetail.store,
      storeDetail.template_id,
    );
    document.title = sanitizedTitle;

    return () => {
      document.title = previousTitle;
    };
  }, [storeDetail]);

  useEffect(() => {
    if (!storePogId) return;

    let active = true;
    const controller = new AbortController();

    // This page also renders headless, unattended, for the batch PDF export — there's no one
    // to notice a one-off network blip and retry manually, and the alternative (silently
    // treating any failure as "everything is new") would produce a misleading document for
    // what's often just a transient error. One retry before giving up.
    const fetchPreviousAssortment = async () => {
      try {
        return await getPreviousAssortmentApi({
          storePogId,
          compareWith,
          signal: controller.signal,
        });
      } catch (err) {
        if (isCancel(err)) throw err;
        return await getPreviousAssortmentApi({
          storePogId,
          compareWith,
          signal: controller.signal,
        });
      }
    };

    fetchPreviousAssortment()
      .then((data) => {
        if (!active || !data) return;

        setHasComparisonError(false);

        // No delivery on record at all is treated the same as a real previous delivery that
        // happened to have zero products — every current item shows as newly added either way
        // — but we still flag it separately so the PDF can say so explicitly, rather than
        // looking identical to "compared against an empty delivery."
        setHasNoDeliveryHistory(data.source === "NONE");

        const productsMap: StoreProductsMap = {};
        for (const product of data.products) {
          productsMap[product.product_code] = { name: product.name, is_oos: false };
        }
        setStoreProducts(productsMap);
      })
      .catch((err) => {
        if (isCancel(err)) return;
        console.error("Failed to fetch previous assortment:", err);

        // getPreviousAssortmentApi already toasts, but that's transient - if this PDF gets
        // saved/printed there'd be no lasting record that the comparison failed. Treat it like
        // an empty delivery (every item shows as new) rather than leaving storeProducts
        // undefined, which would silently fall back to an unrelated legacy new/deleted-flag
        // mechanism instead of clearly explaining why nothing was compared.
        if (!active) return;
        setHasComparisonError(true);
        setStoreProducts({});
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [storePogId, compareWith]);

  return (
    <Page hasSidebar={false}>
      {isLoading && <PageStatus label="Loading pog pdf..." icon="spinner" />}
      {!isLoading && pog && (
        <PdfPlanogramPage
          pogAtom={pogAtom}
          storeDetail={storeDetail}
          storeProducts={storeProducts}
          hasNoDeliveryHistory={hasNoDeliveryHistory}
          hasComparisonError={hasComparisonError}
        />
      )}
    </Page>
  );
};
